import { gql } from '@apollo/client';
import { FormLabel, Stack, useToast } from '@chakra-ui/react';
import { isNil } from 'lodash';
import { useTranslation } from 'react-i18next';
import { toAvailableControlToggleOptions } from '~components/displays/utils';
import { FormLabelPendingIndicator } from '~components/ui/FormLabelPendingIndicator';
import { ConfigPortsControlLockState } from '~graphql/__generated__/types';
import { fromError } from '~utils/errors';
import {
  ConfigDisplayControlsToggle,
  ConfigToggleOptionsConfig,
} from '../ConfigDisplayControlsToggle';
import {
  ConfigDisplayPortsControlToggle_ConfigFragment,
  useConfigUpdatePortsControlMutation,
} from './__generated__/ConfigDisplayPortsControlToggle.graphql';

interface Props {
  config: ConfigDisplayPortsControlToggle_ConfigFragment;
  isDisabled?: boolean;
}

export function ConfigDisplayPortsControlToggle({ config, isDisabled = false }: Props) {
  const toast = useToast();
  const { t } = useTranslation();
  const [updateConfigPortsControlState, { loading }] = useConfigUpdatePortsControlMutation();

  const handleChange = async (portsControlState: ConfigPortsControlLockState) => {
    const { id } = config;

    try {
      updateConfigPortsControlState({
        variables: {
          input: {
            configurationId: id,
            portsControlState,
          },
        },
        refetchQueries: ['CustomerConfigDetailPage'],
        awaitRefetchQueries: true,
      });
    } catch (error) {
      toast({
        status: 'error',
        title: t('controlUpdateErr'),
        description: fromError(error, 'ConfigUpdatePortsControl'),
      });
    }
  };

  const portsControlOptions: Array<ConfigToggleOptionsConfig<ConfigPortsControlLockState>> = [
    { value: ConfigPortsControlLockState.Locked, label: t('lockAllPorts') },
    { value: ConfigPortsControlLockState.Unlocked, label: t('unlockAllPorts') },
  ];
  const currentControlState = config.portsControl?.reported;

  const controlOptions = toAvailableControlToggleOptions(
    portsControlOptions,
    config.portsControl?.supportedValues,
  );

  if (isNil(currentControlState)) {
    return null;
  }

  return (
    <Stack direction="column" spacing="3">
      <FormLabel>
        {t('lockUSBMicroSD')}
        <FormLabelPendingIndicator isPending={loading} />
      </FormLabel>

      <ConfigDisplayControlsToggle
        name="portsControlState"
        value={currentControlState}
        handleChange={handleChange}
        isLoading={loading}
        options={controlOptions}
        isDisabled={isDisabled}
      />
    </Stack>
  );
}

ConfigDisplayPortsControlToggle.graphql = {
  fragments: {
    ConfigDisplayInfraRedControlToggle_config: gql`
      fragment ConfigDisplayPortsControlToggle_config on ConfigurationModel {
        id
        portsControl {
          reported
          supportedValues
        }
      }
    `,
  },
  mutations: {
    ConfigUpdateInfraRedControl: gql`
      mutation ConfigUpdatePortsControl($input: ConfigurationUpdatePortsControlInput!) {
        configurationBulkUpdatePortsControl(input: $input) {
          id
          portsControl {
            reported
            supportedValues
          }
        }
      }
    `,
  },
};
